/**
 * What a save would do, field by field, for the dialog that asks before it happens.
 *
 * Read off the same state the payload is built from -- the value diff, and the two sets that say
 * which fields join or leave the layer -- so the list cannot describe a save the form would not
 * make. The grouping is the point: a value moving and a value stopping being set here are different
 * things, and one flat list of field names says neither.
 */

import { Field, FieldValue, FieldValues } from '../schema';
import { configKeysForField } from './configMappers';
import { printer, valuePrintable } from './printValue';
import { t } from '~i18n';

export interface StagedChange {
  id: string;
  label: string;
  /** What the field reads as now. Absent where it is the same as `to`, or has no printable form. */
  from?: string;
  /** What it will read as once saved. Absent for a value with no printable form. */
  to?: string;
}

export interface StagedChanges {
  /** The value moved. */
  edited: StagedChange[];
  /** Leaving this layer, back to whatever it inherits. */
  cleared: StagedChange[];
  /** Joining this layer, at the value on screen. */
  pinned: StagedChange[];
}

export interface StagedChangesInput {
  fields: Map<string, Field>;
  values: FieldValues;
  initialValues: FieldValues;
  /** Field ids whose value differs from the one the form was seeded with. */
  dirty: string[];
  clearedKeys: string[];
  pinnedKeys: string[];
  /** What a cleared field falls back to, so the dialog can name it rather than say "unset". */
  fallbackValues: FieldValues;
}

const change = (field: Field, from: FieldValue, to: FieldValue): StagedChange => {
  const row: StagedChange = { id: field.id, label: t(field.title) };
  // A polygon is real but unprintable, so the row says the field changed and stops there rather
  // than printing [object Object] at either end.
  if (!valuePrintable(field)) return row;
  const print = printer(field);
  row.to = print(to);
  const printedFrom = print(from);
  // Only worth a before where it differs: pinning an inherited value writes it unchanged, and
  // "30 to 30" would read as a mistake rather than as the point of the action.
  if (printedFrom !== row.to) row.from = printedFrom;
  return row;
};

export const buildStagedChanges = ({
  fields,
  values,
  initialValues,
  dirty,
  clearedKeys,
  pinnedKeys,
  fallbackValues,
}: StagedChangesInput): StagedChanges => {
  const edited: StagedChange[] = [];
  const cleared: StagedChange[] = [];
  const pinned: StagedChange[] = [];

  const staged = new Set<string>();
  for (const field of fields.values()) {
    const keys = configKeysForField(field);
    // Clearing wins where a field somehow has keys in both sets: it is the one that removes
    // something, and the payload resolves it the same way -- the nulls are written last.
    if (keys.some((key) => clearedKeys.includes(key))) {
      staged.add(field.id);
      cleared.push(change(field, initialValues[field.id], fallbackValues[field.id]));
    } else if (keys.some((key) => pinnedKeys.includes(key))) {
      staged.add(field.id);
      pinned.push(change(field, initialValues[field.id], values[field.id]));
    }
  }

  for (const id of dirty) {
    const field = fields.get(id);
    // A field the schema does not describe cannot be named, and a staged one is already listed
    // under what staging it does -- which is the reason its value moved.
    if (!field || staged.has(id)) continue;
    edited.push(change(field, initialValues[id], values[id]));
  }

  return { edited, cleared, pinned };
};

export const changeCount = (changes: StagedChanges): number =>
  changes.edited.length + changes.cleared.length + changes.pinned.length;
